import { deathMarkOpacity } from "./deathMarkOpacity";
import { isDeadAt } from "./isDeadAt";
import type { MapLayer } from "./MAP_LAYERS";
import type { MapColors } from "./MapColors";
import type { MapMode } from "./MapMode";
import type { MapScene } from "./MapScene";
import type { MapView } from "./MapView";
import {
  bossMark,
  buffMark,
  campMark,
  deathMark,
  objectiveMark,
  playerMark,
  runeMark,
  sentryMark,
  shopMark,
  trailSegment,
  ZIPLINE_WIDTH,
} from "./marks";
import { pointAt } from "./pointAt";
import { circle, ring } from "./shapes";
import { WORLD_SIZE } from "./WORLD_SIZE";

// Картинки сцены: миникарта, иконки лагерей по виду и статусу, лица героев, тепло.
export type MapImages = {
  minimap: CanvasImageSource | null;
  // Ключ — `вид:статус`, например `vault:me`.
  camps: ReadonlyMap<string, CanvasImageSource>;
  heroes: ReadonlyMap<number, CanvasImageSource>;
  heat: CanvasImageSource;
};

export type MapFrame = {
  t: number;
  mode: MapMode;
  layers: Readonly<Record<MapLayer, boolean>>;
  // Место события из карточки на карте — подсвечивается кольцом.
  highlight: { x: number; y: number } | null;
};

type Canvas = { width: number; height: number; dpr: number };

const CENTER = WORLD_SIZE / 2;
// Мид-босс появляется на 10-й минуте и возрождается через 7 минут; бафф живёт 3 минуты.
const BOSS_SPAWN_S = 600;
const BOSS_RESPAWN_S = 420;
const BOSS_BUFF_S = 180;
// Хвост игрока — последняя минута, 90 точек; скачок дальше 40 единиц (возрождение,
// телепорт) хвост обрывает.
const TRAIL_POINTS = 90;
const TRAIL_SECONDS = 60;
const TRAIL_JUMP = 40;

// Тепловая карта матча в момент `frame.t`: всё в единицах сцены, преобразование — `view`.
// У команды 1 карта повёрнута на 180°, иконки при этом остаются ровными.
export const drawMatchMap = (
  context: CanvasRenderingContext2D,
  canvas: Canvas,
  view: MapView,
  scene: MapScene,
  frame: MapFrame,
  images: MapImages,
  colors: MapColors,
): void => {
  const { t, mode, layers } = frame;
  const flipped = scene.team === 1;
  const sign = flipped ? -1 : 1;
  const shift = flipped ? view.scale * WORLD_SIZE : 0;
  const teamColor = (team: number) => colors.teams[team] ?? colors.mate;

  context.setTransform(canvas.dpr, 0, 0, canvas.dpr, 0, 0);
  context.fillStyle = colors.backdrop;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const scale = view.scale * canvas.dpr * sign;
  context.setTransform(
    scale,
    0,
    0,
    scale,
    (view.ox + shift) * canvas.dpr,
    (view.oy + shift) * canvas.dpr,
  );

  // Основа: тёмный круг и миникарта, поверх — зиплайны в цветах половин карты.
  circle(context, CENTER, CENTER, WORLD_SIZE * 0.56, colors.ground);
  if (images.minimap !== null) {
    context.drawImage(images.minimap, 0, 0, WORLD_SIZE, WORLD_SIZE);
  }
  drawZiplines(context, scene, colors);

  context.imageSmoothingEnabled = true;
  context.drawImage(images.heat, 0, 0, WORLD_SIZE, WORLD_SIZE);

  for (const camp of scene.camps) {
    campMark(context, flipped, camp, images.camps.get(`${camp.kind}:${camp.status}`), colors);
  }

  // Здания стоят, пока их не разрушили; стражи базы — всегда.
  for (const objective of scene.objectives) {
    if (objective.destroyedAtS !== null && t >= objective.destroyedAtS) {
      continue;
    }
    objectiveMark(
      context,
      objective.x,
      objective.y,
      objective.kind,
      teamColor(objective.team),
      colors,
    );
  }
  for (const sentry of scene.sentries) {
    sentryMark(context, sentry.x, sentry.y, teamColor(sentry.team), colors);
  }
  for (const [x, y] of scene.shops) {
    shopMark(context, x, y, colors);
  }

  if (layers.boss) {
    drawMidBoss(context, scene, t, colors);
  }
  if (layers.runes) {
    for (const rune of scene.runes) {
      if (t >= rune.from && t < rune.to) {
        runeMark(context, rune.x, rune.y, colors);
      }
    }
  }

  // Смерти: твои — крупные красные, твои убийства — золотые, остальные — мелкие в цвете
  // команды погибшего. Слой «Чужие смерти» прячет все, кроме твоих.
  for (const mark of scene.deathMarks) {
    if (mark.kind !== "me" && !layers.deaths) {
      continue;
    }
    const opacity = deathMarkOpacity(mark, t);
    if (opacity <= 0) {
      continue;
    }
    context.globalAlpha = opacity;
    const color =
      mark.kind === "me"
        ? colors.missed
        : mark.kind === "kill"
          ? colors.kill
          : teamColor(mark.victimTeam);
    deathMark(context, mark.x, mark.y, mark.kind, color, colors);
    context.globalAlpha = 1;
  }

  // Остальные игроки — лицо героя на круге цвета команды, пока живы. В «весь матч» в конце
  // матча их нет: позиции важны только для выбранного момента.
  const live = mode === "upto" || t < scene.durationS;
  for (const player of scene.others) {
    const layer: MapLayer = player.team === scene.team ? "mates" : "enemies";
    const point = pointAt(player.track, scene.stepS, t);
    if (!live || !layers[layer] || point === null || isDeadAt(player.deaths, t)) {
      continue;
    }
    playerMark(
      context,
      flipped,
      point[0],
      point[1],
      "other",
      teamColor(player.team),
      images.heroes.get(player.heroId),
      colors,
    );
  }

  drawMe(context, scene, t, live, flipped, images, colors);

  if (frame.highlight !== null) {
    context.globalAlpha = 0.22;
    circle(context, frame.highlight.x, frame.highlight.y, 36, colors.accent);
    context.globalAlpha = 1;
    ring(context, frame.highlight.x, frame.highlight.y, 36, 4, colors.accent);
  }
};

const drawZiplines = (context: CanvasRenderingContext2D, scene: MapScene, colors: MapColors) => {
  // Нижняя половина линии — команды 0, верхняя — команды 1, как в игре.
  context.lineWidth = ZIPLINE_WIDTH;
  context.lineCap = "round";
  context.lineJoin = "round";
  for (const [half, color] of [
    [0, colors.teams[0]],
    [1, colors.teams[1]],
  ] as const) {
    context.save();
    context.beginPath();
    // Точки зиплайнов чуть выходят за край миникарты — берём с запасом.
    context.rect(-WORLD_SIZE, half === 0 ? CENTER : -WORLD_SIZE, 3 * WORLD_SIZE, 1.5 * WORLD_SIZE);
    context.clip();
    context.strokeStyle = color;
    for (const points of scene.ziplines) {
      smoothLine(context, points);
      context.stroke();
    }
    context.restore();
  }
};

const drawMidBoss = (
  context: CanvasRenderingContext2D,
  scene: MapScene,
  t: number,
  colors: MapColors,
) => {
  const alive =
    t >= BOSS_SPAWN_S &&
    !scene.midBoss.some((kill) => t >= kill.killed_at_s && t < kill.killed_at_s + BOSS_RESPAWN_S);
  bossMark(context, CENTER, CENTER, alive, colors);
  // После убийства — кристалл баффа в цвете команды, которая его забрала.
  for (const kill of scene.midBoss) {
    if (t >= kill.killed_at_s && t < kill.killed_at_s + BOSS_BUFF_S) {
      buffMark(
        context,
        CENTER,
        CENTER + 45,
        colors.teams[kill.team_claimed] ?? colors.mate,
        colors,
      );
    }
  }
};

const drawMe = (
  context: CanvasRenderingContext2D,
  scene: MapScene,
  t: number,
  live: boolean,
  flipped: boolean,
  images: MapImages,
  colors: MapColors,
) => {
  const point = pointAt(scene.me.track, scene.stepS, t);
  if (!live || point === null || isDeadAt(scene.me.deaths, t)) {
    return;
  }
  // Хвост за последнюю минуту: тоньше и прозрачнее к началу, после скачка начинается заново.
  const trail = Array.from({ length: TRAIL_POINTS }, (_, k) =>
    pointAt(
      scene.me.track,
      scene.stepS,
      Math.max(0, t - (TRAIL_POINTS - 1 - k) * (TRAIL_SECONDS / TRAIL_POINTS)),
    ),
  );
  let cut = 0;
  for (let k = 1; k < TRAIL_POINTS; k += 1) {
    const previous = trail[k - 1] ?? null;
    const current = trail[k] ?? null;
    if (
      previous === null ||
      current === null ||
      Math.hypot(current[0] - previous[0], current[1] - previous[1]) > TRAIL_JUMP
    ) {
      cut = k;
    }
  }
  for (let k = Math.max(cut, 1); k < TRAIL_POINTS; k += 1) {
    const from = trail[k - 1];
    const to = trail[k];
    if (from === null || from === undefined || to === null || to === undefined) {
      continue;
    }
    trailSegment(
      context,
      [from[0], from[1]],
      [to[0], to[1]],
      k / TRAIL_POINTS,
      ((k - cut) / (TRAIL_POINTS - cut)) ** 1.4,
      colors,
    );
  }
  playerMark(
    context,
    flipped,
    point[0],
    point[1],
    "me",
    colors.teams[scene.team] ?? colors.me,
    images.heroes.get(scene.me.heroId),
    colors,
  );
};

// Плавная линия через точки: квадратичные кривые через середины отрезков.
const smoothLine = (
  context: CanvasRenderingContext2D,
  points: readonly (readonly [number, number])[],
) => {
  context.beginPath();
  const [first, ...rest] = points;
  if (first === undefined) {
    return;
  }
  context.moveTo(first[0], first[1]);
  rest.forEach((point, index) => {
    const next = rest[index + 1];
    if (next === undefined) {
      context.lineTo(point[0], point[1]);
      return;
    }
    context.quadraticCurveTo(
      point[0],
      point[1],
      (point[0] + next[0]) / 2,
      (point[1] + next[1]) / 2,
    );
  });
};
